import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IModalConfig } from '../../../../../types/modal';
import { useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Modal from '../../../modal/Modal';
import Button from 'devextreme-react/button';
import icons from '../../../icons/icons';
import DistanceCalculatorForm from '../distance-calculator-form/DistanceCalculatorForm';
import { MarsApiConfig as apiConfig } from '../../../../../api/mars-api-config';
import { MarsApiService as api } from '../../../../../api/mars-api-service';
import { useDateFormat } from '../../../../../hooks/useDateFormat';
import { useLoaderContext } from '../../../../../contexts/LoaderContext';
import ValidationGroup from 'devextreme-react/validation-group';
import { IDistanceCalculationInfo } from '../../../../../types/distance-calculation-info';
import { DistanceCalculationResponse } from '../../../../../types/distance-calculation-info';

export default function DistanceCalculatorControl({
  onClose,
  onSave,
}: Readonly<{
  onClose: (value: boolean) => void;
  onSave?: (data: IDistanceCalculationInfo) => void;
}>) {
  const [results, setResults] = useState<DistanceCalculationResponse | null>(
    null
  );

  const validationGroup: any = useRef();
  const { setIsLoading } = useLoaderContext();
  const { apiDateTimeFormat } = useDateFormat();
  const { apiDateAndTimeFormat } = useDateFormat();
  const { t } = useTranslation();
  const modalConfig: IModalConfig = {
    width: 1110,
    height: results ? 380 : 270,
    visible: true,
    showTitle: false,
    closeOnOutsideClick: false,
  };

  const popupAttributes = useMemo(() => {
    return {
      id: 'elementId',
      class: 'm-l-modal-main__wrap m-l-add-new-modal',
    };
  }, []);

  const defaultFormValues: IDistanceCalculationInfo = {
    fromPort: null,
    toPort: null,
    fromLat: null,
    fromLon: null,
    toLat: null,
    toLon: null,
    gateways: null,
    speed: null,
    startAt: null,
    useToCoordinates: false,
    useFromCoordinates: false,
  };

  const customDistanceForm = useForm<{
    fromPort: string | null;
    toPort: string | null;
    fromLat: number | null;
    fromLon: number | null;
    toLat: number | null;
    toLon: number | null;
    gateways: string | null;
    speed: number | null;
    startAt: Date | null;
    useToCoordinates: boolean;
    useFromCoordinates: boolean;
  }>({
    defaultValues: defaultFormValues,
  });

  const { isDirty } = customDistanceForm.formState;

  const onClickCalculateDistance = async () => {
    if (validationGroup.current?.instance.validate().isValid) {
      const formValues = customDistanceForm.getValues();
      const payload = {
        fromPort: formValues.fromPort,
        toPort: formValues.toPort,
        fromLat: formValues.fromLat,
        fromLon: formValues.fromLon,
        toLat: formValues.toLat,
        toLon: formValues.toLon,
        gateways: Array.isArray(formValues.gateways)
          ? formValues.gateways.join(',')
          : formValues.gateways || '',
        speed: formValues.speed,
        startAt: apiDateTimeFormat(formValues.startAt) || new Date(),
        useToCoordinates: formValues.useToCoordinates,
        useFromCoordinates: formValues.useFromCoordinates,
      };
      try {
        const response: DistanceCalculationResponse = await api.post(
          {
            url: apiConfig.distanceCalculationBetweenPorts,
            data: payload,
          },
          setIsLoading
        );
        setResults(response);
        if (
          formValues.startAt &&
          formValues.speed !== null &&
          formValues.fromLat !== null &&
          formValues.fromLon !== null &&
          formValues.toLat !== null &&
          formValues.toLon !== null
        ) {
          onSave?.({
            fromPort: formValues.fromPort,
            toPort: formValues.toPort,
            fromLat: formValues.fromLat,
            fromLon: formValues.fromLon,
            toLat: formValues.toLat,
            toLon: formValues.toLon,
            gateways: formValues.gateways || '',
            speed: formValues.speed,
            startAt: formValues.startAt || new Date(),
            useToCoordinates: formValues.useToCoordinates,
            useFromCoordinates: formValues.useFromCoordinates,
          });
        }
      } catch (error) {
        setResults(null);
      }
    }
  };

  const handleClose = () => {
    if (isDirty) {
      customDistanceForm.reset(defaultFormValues);
    }
    onClose?.(false);
  };

  return (
    <Modal modalConfig={modalConfig} wrapperAttr={popupAttributes}>
      <div className="m-l-modal__header">
        <h2 className="modal-title"> {t('headers.distanceCalculator')}</h2>
        <div className="m-l-modal-close-button-block">
          <Button
            className="app-c-btn app-c-icon-only-btn"
            onClick={handleClose}
          >
            <icons.IconCross />
          </Button>
        </div>
      </div>
      <div className="m-l-modal__body">
        <ValidationGroup ref={validationGroup}>
          <FormProvider {...customDistanceForm}>
            <DistanceCalculatorForm />
          </FormProvider>
        </ValidationGroup>
      </div>
      {results && (
        <div
          className={`m-l-results-section ${
            !results.eta ? 'result-distance' : ''
          }`}
        >
          <div className="d-flex flex-column align-items-center">
            <div className="m-l-ads-tbl-title">{t('labels.distanceNM')}</div>
            <div className="m-l-input-data-preview">
              {`${results.distance} NM`}
            </div>
          </div>
          {results.eta && (
            <div className="d-flex flex-column align-items-center m-l-ads-table-block m-l-ads-table-items">
              <div className="m-l-ads-tbl-title">{t('labels.eta')}</div>
              <div className="m-l-input-data-preview">
                {apiDateAndTimeFormat(results.eta)}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="m-l-modal__footer pb-2">
        <Button
          className="app-c-btn app-c-btn--primary min-btn-width"
          onClick={onClickCalculateDistance}
        >
          {t('buttons.calculate')}
        </Button>
      </div>
    </Modal>
  );
}
